import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    getBluedartApprovalFilterOptions,
    getBluedartApprovalRequests,
    getBluedartFilterVendors,
    approveBluedartRequest,
    rejectBluedartRequest,
} from '../../store/actions/staffActionCreator';
import { showMessage } from '../../utils/helper';
import { getPermissions } from '../../utils/permissions';
import {
    isBlueDartRiderViewer,
    isBlueDartRiderApprover,
} from '../../utils/appHelper';
import useApprovalWorkflowStyles from './useApprovalWorkflowStyles';

const DEFAULT_PAGE_SIZE = 20;

const useApprovalWorkflow = () => {
    const dispatch = useDispatch();
    const styles = useApprovalWorkflowStyles();

    const [requestType, setRequestType] = useState('ONBOARDING');
    const [status, setStatus] = useState('PENDING');
    const [requests, setRequests] = useState<any[]>([]);
    const [counts, setCounts] = useState<Record<string, number>>({});
    const [totalCount, setTotalCount] = useState(0);
    // The three filters, and what may be offered in them. Options come from the requests that
    // exist, so a filter never offers a value that would return an empty queue.
    const [orgIds, setOrgIds] = useState<any[]>([]);
    const [storeIds, setStoreIds] = useState<string[]>([]);
    const [zones, setZones] = useState<string[]>([]);
    // Stores and zones come from the approval endpoint, which reads hubs — the same master a
    // request snapshots its store and zone from. Vendors reuse the shared BD vendor list.
    const [storeOptions, setStoreOptions] = useState<string[]>([]);
    const [zoneOptions, setZoneOptions] = useState<string[]>([]);

    // Vendors come from the shared BD vendor list, already narrowed to the caller's scope by the
    // same rule Rider Management uses. Only that page dispatches it, so the Approval tab has to
    // ask too — landing here directly would otherwise leave the dropdown empty.
    const vendorOrgs = useSelector(
        (state: any) => state?.staff?.bdFilterVendors ?? [],
    );

    const [pageNo, setPageNo] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [loading, setLoading] = useState(false);

    // Server-side kill switch for the rejoining confirmation; absent means on, so a response
    // that predates it still asks rather than silently dropping the warning.
    const [rehireConfirmationRequired, setRehireConfirmationRequired] =
        useState(true);

    const [decisionRow, setDecisionRow] = useState<any>(null);
    const [detailRow, setDetailRow] = useState<any>(null);

    const permissions = useMemo(() => getPermissions('approval_workflow'), []);

    /**
     * AP-5: a Rider Viewer sees the queue but decides nothing. A Rider Approver may decide an
     * onboarding but never a separation — the backend refuses either way, this only keeps the
     * buttons off screen so nobody clicks into a 403.
     */
    const canDecide = useMemo(() => {
        if (!permissions.update || isBlueDartRiderViewer()) return false;
        if (requestType !== 'ONBOARDING' && isBlueDartRiderApprover())
            return false;
        return true;
    }, [permissions.update, requestType]);

    const fetchRequests = useCallback(() => {
        setLoading(true);
        dispatch(
            getBluedartApprovalRequests(
                {
                    requestType,
                    status,
                    orgIds: orgIds.map((o: any) => o?.orgId ?? o),
                    storeIds,
                    zones,
                    pageNo,
                    pageSize,
                },
                (response: any) => {
                    setLoading(false);
                    const body = response?.response ?? {};
                    setRequests(body.requests ?? []);
                    setTotalCount(body.totalCount ?? 0);
                    setCounts(body.counts ?? {});
                    setRehireConfirmationRequired(
                        body.rehireIneligibleConfirmationRequired !== false,
                    );
                },
                (error: any) => {
                    setLoading(false);
                    setRequests([]);
                    setTotalCount(0);
                    showMessage(
                        error?.status?.message ?? 'Could not load requests',
                        'error',
                    );
                },
            ),
        );
    }, [dispatch, requestType, status, orgIds, storeIds, zones, pageNo, pageSize]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    // Once per mount. The option set is scope-derived, so it does not change as the user pages
    // or switches type — refetching per tab would be three identical calls.
    useEffect(() => {
        dispatch(
            getBluedartApprovalFilterOptions(
                (response: any) => {
                    const body = response?.response ?? {};
                    setStoreOptions(body.storeIds ?? []);
                    setZoneOptions(body.zones ?? []);
                },
                // A failed lookup costs the dropdowns, not the queue.
                () => {
                    setStoreOptions([]);
                    setZoneOptions([]);
                },
            ),
        );
        dispatch(getBluedartFilterVendors());
    }, [dispatch]);

    // Any filter change shortens the result set, so page 3 of the old one would render empty.
    const handleFilterChange = useCallback(
        (setter: (v: any) => void) => (value: any) => {
            setter(value);
            setPageNo(0);
        },
        [],
    );

    const handleTypeChange = (next: string) => {
        setRequestType(next);
        setStatus('PENDING');
        setPageNo(0);
    };

    const handleStatusChange = (next: string) => {
        setStatus(next);
        setPageNo(0);
    };

    const handleChangePage = (_e: unknown, next: number) => setPageNo(next);

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setPageSize(parseInt(event.target.value, 10));
        setPageNo(0);
    };

    // One object rather than a row of positional strings: the decision now carries an
    // acknowledgement too, and a bare boolean fifth argument is the kind of thing that gets
    // passed in the wrong slot.
    const handleApprove = (
        requestRef: string,
        decision: {
            additionType?: string;
            replacementEmpId?: string;
            remark?: string;
            rehireIneligibleConfirmed?: boolean;
        },
        onDone: () => void,
    ) => {
        dispatch(
            approveBluedartRequest(
                requestRef,
                decision,
                () => {
                    showMessage('Request approved', 'success');
                    onDone();
                    setDecisionRow(null);
                    fetchRequests();
                },
                (error: any) => {
                    onDone();
                    // A replay can fail on data that changed since submission; the backend keeps
                    // the request pending and returns why, so it is shown verbatim.
                    showMessage(
                        error?.status?.message ??
                            'Could not approve this request',
                        'error',
                    );
                    fetchRequests();
                },
            ),
        );
    };

    const handleReject = (
        requestRef: string,
        remark: string,
        onDone: () => void,
    ) => {
        dispatch(
            rejectBluedartRequest(
                requestRef,
                { remark },
                () => {
                    showMessage('Request rejected', 'success');
                    onDone();
                    setDecisionRow(null);
                    fetchRequests();
                },
                (error: any) => {
                    onDone();
                    showMessage(
                        error?.status?.message ??
                            'Could not reject this request',
                        'error',
                    );
                },
            ),
        );
    };

    return {
        styles,
        permissions,
        canDecide,
        requestType,
        status,
        requests,
        counts,
        totalCount,
        orgIds,
        storeIds,
        zones,
        storeOptions,
        zoneOptions,
        vendorOrgs,
        setOrgIds: handleFilterChange(setOrgIds),
        setStoreIds: handleFilterChange(setStoreIds),
        setZones: handleFilterChange(setZones),
        pageNo,
        pageSize,
        loading,
        rehireConfirmationRequired,
        decisionRow,
        setDecisionRow,
        detailRow,
        setDetailRow,
        handleTypeChange,
        handleStatusChange,
        handleChangePage,
        handleChangeRowsPerPage,
        handleApprove,
        handleReject,
        refetch: fetchRequests,
    };
};

export default useApprovalWorkflow;
